/* @flow */
import * as React from 'react';
import invariant from 'invariant';

import AddGroupOwnerModalConfirm from 'groups/components/AddGroupOwnerModalConfirm.jsx';
import BatchRoleCorrectionModal from 'users/components/BatchRoleCorrectionModal.jsx';
import BlockMemberMenuItem from 'users/components/BlockMemberMenuItem.jsx';
import GetTranscriptMenuItem from 'user/components/GetTranscriptMenuItem.jsx';
import Glyph from 'components/Glyph.jsx';
import Group from 'entities/Group';
import GroupMembership from 'entities/GroupMembership';
import Menu from 'components/Menu.jsx';
import MenuButton from 'components/MenuButton.jsx';
import ModalMenuItem from 'components/ModalMenuItem.jsx';
import RelationshipModal from 'user/components/RelationshipModal.jsx';
import RemoveFromOrgModal from 'organizations/components/RemoveFromOrgModal.jsx';
import RemoveMemberMenuItem from 'groups/components/RemoveMemberMenuItem.jsx';
import ReportOrgUserModal from 'organizations/components/ReportOrgUserModal.jsx';
import ReportUserMenuItem from 'user/components/ReportUserMenuItem.jsx';
import Subscriber from 'entities/Subscriber';
import SubscribeUserMenuItem from 'user/components/SubscribeUserMenuItem.jsx';

type Props = {
  glyphSize?: string,
  buttonSize?: string,
  className?: string,
  groupMembership?: GroupMembership,
  subscriber: Subscriber,
  groupToAdmin?: Group,
  handleClick?: Function,
  level?: string,
  showViewProfile?: boolean,
  tooltip?: string,
  tooltipPlacement?: string,
  disabled?: ?boolean,
  isAdminView?: ?boolean,
  onUserUpdate?: () => any,
  onUserRemoved?: () => any,

  showChangeRole?: boolean,
  showViewProfile?: boolean,
  showCopyToClass?: boolean,
  showMakeOwner?: boolean,
  showRemoveMember?: boolean,
  showBlockMember?: boolean,
  showGetTranscript?: boolean,
  showRemoveFromSchool?: boolean,
  showReportAsUser?: boolean,
  showReportAsAdmin?: boolean,

  disableChangeRole?: boolean,
  disableViewProfile?: boolean,
  disableCopyToClass?: boolean,
  disableMakeOwner?: boolean,
  disableRemoveMember?: boolean,
  disableBlockMember?: boolean,
  disableGetTranscript?: boolean,
  disableRemoveFromSchool?: boolean,
  disableReportAsUser?: boolean,
  disableReportAsAdmin?: boolean,
};

class UserActionsMenuButton extends React.Component<Props> {
  static defaultProps = {
    level: 'secondary',
  };

  onUserUpdate = () => {
    if (this.props.onUserUpdate) {
      this.props.onUserUpdate();
    }
  };
  onUserRemoved = () => {
    if (this.props.onUserRemoved) {
      this.props.onUserRemoved();
    }
  };
  renderMakeOwnerDialog = () => {
    const { groupMembership, groupToAdmin } = this.props;
    invariant(groupMembership, 'renderMakeOwnerDialog called without a group membership');
    return (
      <AddGroupOwnerModalConfirm
        group={groupToAdmin}
        checkedIds={[groupMembership.getUser().id]}
        userName={groupMembership.getUser().getName()}
      />
    );
  };

  renderChangeRoleModal = () => {
    const { groupMembership, groupToAdmin } = this.props;
    invariant(groupToAdmin, 'renderChangeRoleModal when no group');
    invariant(groupMembership, 'renderChangeRoleModal called without a group membership');
    return (
      <BatchRoleCorrectionModal
        group={groupToAdmin}
        groupMemberships={[groupMembership]}
        onUserRoleSave={this.onUserUpdate}
      />
    );
  };

  renderMenu = () => {
    const { groupMembership, groupToAdmin, subscriber, isAdminView } = this.props;

    return (
      <Menu>
        {this.props.showChangeRole && (
          <ModalMenuItem
            eventElementName="user_action_menu_change_role"
            modal={this.renderChangeRoleModal()}
            disabled={this.props.disableChangeRole}>
            <Glyph name="spreadsheet" />
            <span>Change role</span>
          </ModalMenuItem>
        )}
        {this.props.showViewProfile && (
          <ModalMenuItem
            eventElementName="user_action_menu_view_profile"
            modal={this.renderRelationshipDialog()}
            disabled={this.props.disableViewProfile}>
            <Glyph name="avatar" />
            <span>View profile</span>
          </ModalMenuItem>
        )}
        {this.props.showCopyToClass && (
          <SubscribeUserMenuItem
            subscriber={subscriber}
            disabled={this.props.disableCopyToClass}
            eventElementName="user_action_menu_copy_to_class"
          />
        )}
        {this.props.showMakeOwner && (
          <ModalMenuItem
            eventElementName="user_action_menu_make_owner"
            modal={this.renderMakeOwnerDialog()}
            disabled={this.props.disableMakeOwner}>
            <Glyph name="crown-stroke" />
            <span>Make owner of class</span>
          </ModalMenuItem>
        )}
        {this.props.showRemoveMember && (
          <RemoveMemberMenuItem
            eventElementName="user_action_menu_remove_member"
            disabled={this.props.disableRemoveMember}
            group={groupToAdmin}
            member={groupMembership}
            onRemove={this.props.onUserRemoved}
          />
        )}
        {this.props.showBlockMember &&
          groupMembership && (
            <BlockMemberMenuItem
              disabled={this.props.disableBlockMember}
              blocked={groupMembership.isBlocked()}
              memberId={groupMembership.getUserId()}
              memberName={groupMembership.getName()}
            />
          )}
        {this.props.showGetTranscript &&
          groupToAdmin &&
          groupMembership && (
            <GetTranscriptMenuItem
              eventElementName="user_action_menu_get_transcript"
              disabled={this.props.disableGetTranscript}
              groupMembership={groupMembership}
              groupToAdmin={groupToAdmin}
              isAdminView={isAdminView}
            />
          )}
        {groupToAdmin &&
          this.props.showRemoveFromSchool && (
            <ModalMenuItem
              eventElementName="user_action_menu_remove_from_school"
              modal={this.renderRemoveFromOrgModal()}
              disabled={this.props.disableRemoveFromSchool}>
              <Glyph name="close" />
              <span>Remove from school</span>
            </ModalMenuItem>
          )}
        {this.props.showReportAsUser && (
          <ReportUserMenuItem
            reportee={subscriber}
            disabled={this.props.disableReportAsUser}
            eventElementName="user_action_menu_report_as_user"
          />
        )}
        {this.props.showReportAsAdmin && (
          <ModalMenuItem
            disabled={this.props.disableReportAsAdmin}
            eventElementName="user_action_menu_report_as_admin"
            modal={this.renderReportOrgUserModal()}>
            <Glyph name="flag" />
            <span>Report to Remind</span>
          </ModalMenuItem>
        )}
      </Menu>
    );
  };

  renderRelationshipDialog = () => {
    const { groupToAdmin, groupMembership } = this.props;
    invariant(groupMembership, 'renderMakeOwnerDialog called without a group membership');
    return (
      <RelationshipModal
        groupToAdmin={groupToAdmin}
        groupMember={groupMembership}
        userId={groupMembership.getUserId()}
      />
    );
  };

  renderRemoveFromOrgModal = () => {
    const { groupToAdmin, groupMembership, isAdminView } = this.props;
    invariant(groupToAdmin, 'renderRemoveFromOrgModal when no group');
    invariant(groupMembership, 'renderRemoveFromOrgModal called without a group membership');
    const org = groupToAdmin.getOrganization();
    invariant(org, 'renderRemoveFromOrgModal failed to get org');
    return (
      <RemoveFromOrgModal
        isAdminView={isAdminView}
        userName={groupMembership.getSubscriber().getName()}
        userIds={[groupMembership.getUserId()]}
        orgName={org.getName()}
        orgId={org.id}
        reportModal={this.renderReportOrgUserModal}
        onRemoved={this.onUserRemoved}
      />
    );
  };

  renderReportOrgUserModal = () => {
    const { groupToAdmin, groupMembership } = this.props;
    invariant(groupToAdmin, 'renderReportOrgUserModal when no group');
    invariant(groupMembership, 'renderReportOrgUserModal called without a group membership');
    const org = groupToAdmin.getOrganization();
    invariant(org, 'renderReportOrgUserModal failed to get org');

    return (
      <ReportOrgUserModal
        userName={groupMembership.getSubscriber().getName()}
        userId={groupMembership.getUserId()}
        orgName={org.getName()}
        orgId={org.id}
        removeModal={this.renderRemoveFromOrgModal}
      />
    );
  };

  render() {
    return (
      <MenuButton
        alignment="right"
        disabled={this.props.disabled}
        className={this.props.className}
        menu={this.renderMenu()}
        size={this.props.buttonSize || 'xsmall'}
        onClick={this.props.handleClick}
        level={this.props.level}
        tooltip={this.props.tooltip}
        tooltipPlacement={this.props.tooltipPlacement}>
        <Glyph
          name="ellipses"
          solo
          // $FlowFixMe match the enum
          size={this.props.glyphSize || 'xlarge'}
        />
      </MenuButton>
    );
  }
}

export default UserActionsMenuButton;
